/**
 * THE CONTRACTOR'S OWN BILL, COMING IN.
 *
 * OVAL issues him three documents — the work order, the variation order and the
 * payment certificate. This is the one that travels the other way: the hard
 * copy he hands over, recorded so that what the company owes him at a site is
 * a figure somebody can read rather than a question somebody has to ask.
 *
 * WHAT HIS INVOICE IS FOR, WHICH IS NOT WHAT HE IS PAID
 * ----------------------------------------------------
 * The full certified value plus GST. Retention, TDS and advance recovery are
 * deductions OVAL makes when PAYING; they are not reductions of what he billed.
 * On RA-1 of WO-TR7 that is 40,000 + 7,200 = 47,200 invoiced against 34,800
 * paid, and the 12,400 between them is retention held, TDS deposited in his
 * name, and advance he already has.
 *
 * So the card shows the ladder rather than a single number, because "you
 * invoiced 47,200 and we paid 34,800" without the three lines in between is
 * exactly the conversation this screen exists to prevent.
 *
 * PARTIAL INVOICES ARE NORMAL
 * ---------------------------
 * A contractor commonly bills a certified amount in two or three pieces. Every
 * invoice against the bill is listed and they add up; rpc_ci_record_invoice
 * refuses to let the total pass what was certified, and says how much is left.
 *
 * THE OFFSET IS AUTOMATIC AND THERE IS NO MATCHING STEP
 * ----------------------------------------------------
 * Both the invoice and the payment carry ra_bill_id, so they meet at the bill
 * with no allocation table between them. Nothing here touches
 * invoice_reconciliations — see migration 0140 for why doing so would zero the
 * paid_amount on a settled contractor payable.
 *
 * WHERE THESE ROWS LIVE, SINCE 0145
 * ---------------------------------
 * contractor_invoices, the Service module's own table. They were in
 * vendor_invoices alongside the materials invoices, filtered apart on
 * ra_bill_id. Board instruction, 19-09-2026: the two modules share only the
 * Vendor master, Sites, UOM and the Terms & Conditions. The screen is
 * unchanged — v_contractor_invoices and the same two RPCs — but the invoice
 * number is now CINV-, and uniqueness of a contractor's invoice number is
 * checked within this book, not across the materials one.
 *
 * ACCOUNTS IS WARNED, NOT BLOCKED
 * -------------------------------
 * Decided 17-09-2026. If money has gone out against a bill with no invoice on
 * file the card says so plainly, and does not stop the payment — a site payment
 * should not stall because the contractor's paperwork is in the post.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle, Download, ExternalLink, FileText, Plus, Receipt, Trash2, Upload,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import {
  Card, Confirm, Field, Input, Modal, Stat, Textarea, useToast,
} from '@/components/ui'
import { saveAttachment } from '@/components/docs/shared'
import {
  listWoPapers, uploadWoPaper, woPaperViewUrl, WO_PAPER_BUCKET, type WoPaper,
} from '../lib/papers'

const n = (v: unknown) => Number(v ?? 0) || 0

/** Mirrors may_book_invoice(). The database is the authority. */
const MAY_BOOK = ['admin', 'procurement_manager', 'procurement_head', 'accounts_spoc']

const KIND = 'CONTRACTOR_INVOICE'

interface Ledger {
  ra_id: string; ra_no: number; is_gst_registered: boolean
  invoiceable: number; invoiced: number; invoice_count: number
  net_payable: number; paid_cash: number
  settled_by_tds: number; settled_by_advance: number; retention_held: number
  balance_due: number; not_yet_invoiced: number
}

interface CiRow {
  id: string; invoice_no: string; invoice_date: string
  taxable_amount: number; gst_amount: number; total_amount: number
  status: string; remarks: string | null
  entered_by_name: string | null; created_at: string
}

export function ContractorInvoices({
  raBillId, status, onChanged,
}: {
  raBillId: string
  /** The bill's own status. Nothing may be invoiced before it is APPROVED. */
  status: string
  onChanged?: () => void
}) {
  const { profile } = useAuth()
  const toast = useToast()

  const [ledger, setLedger] = useState<Ledger | null>(null)
  const [rows, setRows] = useState<CiRow[]>([])
  const [scans, setScans] = useState<WoPaper[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [removing, setRemoving] = useState<CiRow | null>(null)

  const [no, setNo] = useState('')
  const [date, setDate] = useState('')
  const [taxable, setTaxable] = useState('')
  const [gst, setGst] = useState('')
  const [remarks, setRemarks] = useState('')

  const canBook = MAY_BOOK.includes(profile?.role ?? '')
  const approved = status === 'APPROVED'

  const load = useCallback(async () => {
    setLoading(true)
    const [{ data: led }, { data: inv }] = await Promise.all([
      supabase.from('v_contractor_bill_ledger').select('*').eq('ra_id', raBillId).maybeSingle(),
      supabase.from('v_contractor_invoices').select('*')
        .eq('ra_bill_id', raBillId).order('invoice_date'),
    ])
    setLedger((led ?? null) as Ledger | null)
    setRows((inv ?? []) as CiRow[])
    setScans((await listWoPapers(raBillId, 'RA')).filter((p) => p.kind === KIND))
    setLoading(false)
  }, [raBillId])

  useEffect(() => { void load() }, [load])

  // Nothing is drawn at all before the bill is approved. There is no invoice
  // against a bill whose value is still open to change, and a card inviting one
  // would teach people the bill means more at DRAFT than it does.
  if (!approved || loading) return null
  if (!ledger) return null

  const unregistered = !ledger.is_gst_registered
  const left = n(ledger.not_yet_invoiced)
  const paidWithNoInvoice = n(ledger.paid_cash) > 0 && ledger.invoice_count === 0

  async function record() {
    if (!no.trim()) { toast.error('His invoice number is required.'); return }
    if (!date) { toast.error('The date on his invoice is required.'); return }
    if (n(taxable) <= 0) { toast.error('The taxable amount must be more than zero.'); return }
    setBusy('save')
    try {
      const { error } = await supabase.rpc('rpc_ci_record_invoice', {
        p_ra_id: raBillId,
        p_invoice_no: no.trim(),
        p_invoice_date: date,
        p_taxable: Number(taxable),
        p_gst: unregistered ? 0 : Number(gst || 0),
        p_remarks: remarks.trim() || null,
      })
      if (error) throw new Error(error.message)
      toast.success(`Invoice ${no.trim()} recorded.`)
      setOpen(false)
      setNo(''); setDate(''); setTaxable(''); setGst(''); setRemarks('')
      await load(); onChanged?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That invoice could not be recorded')
    } finally { setBusy(null) }
  }

  async function remove(r: CiRow, why: string) {
    setBusy(r.id)
    try {
      const { error } = await supabase.rpc('rpc_ci_delete_invoice',
        { p_invoice_id: r.id, p_reason: why })
      if (error) throw new Error(error.message)
      toast.success('Removed.'); setRemoving(null)
      await load(); onChanged?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'It could not be removed')
    } finally { setBusy(null) }
  }

  async function attach(file: File) {
    setBusy('scan')
    try {
      await uploadWoPaper(raBillId, KIND, file, 'RA')
      toast.success('Scan attached.')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That file could not be attached')
    } finally { setBusy(null) }
  }

  /** The ladder, so the gap between invoiced and paid is never a mystery. */
  const L = ({ label, amount, hint, tone }: {
    label: string; amount: number; hint?: string; tone?: 'less' | 'total'
  }) => (
    <div className={tone === 'total'
      ? 'flex items-baseline justify-between gap-3 border-t border-slate-300 pt-1.5'
      : 'flex items-baseline justify-between gap-3'}>
      <span className="min-w-0">
        <span className={tone === 'total' ? 'text-sm font-semibold text-slate-800' : 'text-sm text-slate-600'}>
          {label}
        </span>
        {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
      </span>
      <span className={
        tone === 'total' ? 'shrink-0 font-semibold tabular-nums text-slate-900'
        : tone === 'less' ? 'shrink-0 tabular-nums text-red-700'
        : 'shrink-0 tabular-nums text-slate-700'}>
        {tone === 'less' && amount !== 0 ? '− ' : ''}{rupees(Math.abs(amount))}
      </span>
    </div>
  )

  return (
    <Card
      title="His invoice, and what is still owed"
      subtitle="The hard copy the contractor hands over. He invoices the certified value plus GST; retention, TDS and advance recovery are deducted when OVAL pays, not from what he bills."
      actions={canBook && left > 0.005 && (
        <button className="btn-primary btn-sm" onClick={() => {
          setGst(unregistered ? '0' : '')
          setOpen(true)
        }}>
          <Plus className="h-4 w-4" /> Record his invoice
        </button>
      )}
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="He may invoice" value={rupees(ledger.invoiceable)}
                sub="Certified value plus GST"
                icon={<Receipt className="h-4 w-4" />} />
          <Stat label="Invoiced so far" value={rupees(ledger.invoiced)}
                sub={ledger.invoice_count === 0 ? 'Nothing on file'
                  : `${ledger.invoice_count} invoice${ledger.invoice_count === 1 ? '' : 's'}`}
                tone={left > 0.005 ? 'warn' : 'default'}
                icon={<FileText className="h-4 w-4" />} />
          <Stat label="Still owed to him" value={rupees(ledger.balance_due)}
                sub="Invoiced, and not yet settled"
                tone={n(ledger.balance_due) > 0.005 ? 'warn' : 'default'}
                icon={<Receipt className="h-4 w-4" />} />
        </div>

        {paidWithNoInvoice && (
          <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{rupees(ledger.paid_cash)} has been paid against this bill and no invoice
              is on file.</strong> The payment stands — it was certified — but the company has
              no tax invoice for it, and on a registered contractor that is an input credit
              sitting unclaimed. Ask him for the bill.
            </span>
          </p>
        )}

        {/* ---------- the ladder ---------- */}
        <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          <L label="Invoiced by the contractor" amount={n(ledger.invoiced)} />
          <L label="Paid in cash" amount={n(ledger.paid_cash)} tone="less"
             hint="What has actually left the bank against this bill" />
          <L label="Tax deducted at source" amount={n(ledger.settled_by_tds)} tone="less"
             hint="Deposited with the Government against his PAN — settled, not owed" />
          <L label="Advance recovered" amount={n(ledger.settled_by_advance)} tone="less"
             hint="Set off against money he already holds — settled, not owed" />
          <L label="Retention held" amount={n(ledger.retention_held)} tone="less"
             hint="Owed, but not due — released under clause 150" />
          <L label="Balance still owed to him" amount={n(ledger.balance_due)} tone="total" />
        </div>

        {left > 0.005 && (
          <p className="text-xs text-slate-500">
            {rupees(left)} of this bill has not been invoiced yet.
            {ledger.invoice_count > 0 && ' He has billed it in parts.'}
          </p>
        )}

        {/* ---------- the invoices ---------- */}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice no.</th><th>Dated</th>
                  <th className="num">Taxable</th><th className="num">GST</th>
                  <th className="num">Total</th><th>Entered by</th>
                  {canBook && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">{r.invoice_no}</td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(r.invoice_date)}</td>
                    <td className="num">{rupees(r.taxable_amount)}</td>
                    <td className="num">{n(r.gst_amount) === 0
                      ? <span className="text-xs text-slate-400">Nil</span>
                      : rupees(r.gst_amount)}</td>
                    <td className="num font-semibold">{rupees(r.total_amount)}</td>
                    <td className="max-w-[140px] truncate text-xs text-slate-500">
                      {r.entered_by_name ?? '—'}
                    </td>
                    {canBook && (
                      <td className="text-right">
                        <button className="btn-ghost btn-sm text-red-600" disabled={busy === r.id}
                                title="Remove this invoice"
                                onClick={() => setRemoving(r)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ---------- the scans ---------- */}
        <div className="border-t border-slate-200 pt-3">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
            The hard copy
          </p>
          {scans.length > 0 ? (
            <div className="space-y-1">
              {scans.map((p) => (
                <div key={p.id}
                     className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate">{p.file_name}</span>
                  <button className="btn-ghost btn-sm shrink-0"
                          onClick={() => void woPaperViewUrl(p.file_path)
                            .then((u) => window.open(u, '_blank', 'noopener'))
                            .catch(() => toast.error('That file could not be opened.'))}>
                    <ExternalLink className="h-4 w-4" /> Open
                  </button>
                  <button className="btn-ghost btn-sm shrink-0"
                          onClick={() => void saveAttachment(WO_PAPER_BUCKET, p.file_path, p.file_name)
                            .catch(() => toast.error('That file could not be downloaded.'))}>
                    <Download className="h-4 w-4" /> Download
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              No scan on file. The figures above are only as good as the paper behind them.
            </p>
          )}
          {canBook && (
            <label className="btn-secondary btn-sm mt-2 cursor-pointer">
              <Upload className="h-4 w-4" />
              {busy === 'scan' ? 'Uploading…' : 'Attach the scan'}
              <input type="file" className="hidden" accept="image/*,application/pdf"
                     disabled={busy === 'scan'}
                     onChange={(e) => {
                       const f = e.target.files?.[0]
                       if (f) void attach(f)
                       e.target.value = ''
                     }} />
            </label>
          )}
        </div>
      </div>

      {/* ---------- recording one ---------- */}
      <Modal open={open} onClose={() => setOpen(false)}
             title={`Record the contractor's invoice against RA-${ledger.ra_no}`}
             subtitle={`Up to ${rupees(left)} may still be invoiced against this bill.`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
                 <button className="btn-primary" disabled={busy === 'save'}
                         onClick={() => void record()}>
                   {busy === 'save' ? 'Saving…' : 'Record it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="His invoice number" required
                   hint="Exactly as written on his bill — it is what he and the tax authorities refer to it by">
              <Input value={no} onChange={(e) => setNo(e.target.value)} />
            </Field>
            <Field label="Dated" required>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Taxable amount" required>
              <Input type="number" step="0.01" value={taxable}
                     onChange={(e) => setTaxable(e.target.value)} />
            </Field>
            <Field label="GST"
                   hint={unregistered
                     ? 'He is not registered, so this supply is under reverse charge — he may not charge GST and OVAL pays it to the Government directly.'
                     : 'As charged on his invoice'}>
              <Input type="number" step="0.01" value={gst} disabled={unregistered}
                     onChange={(e) => setGst(e.target.value)} />
            </Field>
          </div>
          <Field label="Remarks" hint="Optional">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Field>
          {n(taxable) > 0 && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
              Invoice total{' '}
              <strong>{rupees(n(taxable) + (unregistered ? 0 : n(gst)))}</strong>
              {n(taxable) + (unregistered ? 0 : n(gst)) > left + 0.005 && (
                <span className="block text-xs text-red-700">
                  That is more than the {rupees(left)} left to invoice against this bill.
                  It will be refused.
                </span>
              )}
            </p>
          )}
        </div>
      </Modal>

      <Confirm open={!!removing} title={`Remove invoice ${removing?.invoice_no ?? ''}?`}
               tone="danger" confirmLabel="Remove it"
               requireReason reasonLabel="Why is it being removed"
               message={<>It comes off the ledger and what OVAL owes him changes accordingly.
                 The removal is recorded against the bill.</>}
               onConfirm={(why) => { if (removing) void remove(removing, why ?? '') }}
               onCancel={() => setRemoving(null)} />
    </Card>
  )
}
